import React from 'react';
import {
  Chip,
  Tooltip,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useGetFirmAsUser } from '@taxassist/api-client';
import { useFirm } from '../../contexts/FirmContext';
import { canViewBasicCreditInfo } from '../../utils/creditPermissions';
import { useAuth } from "../../contexts/AuthContext";

interface CreditBadgeProps {
  /**
   * Size of the badge
   */
  size?: 'small' | 'medium';
  /**
   * Whether to show the credit count or just an icon
   */
  showCount?: boolean;
  /**
   * Custom click handler for the badge
   */
  onClick?: () => void;
}

/**
 * CreditBadge displays the current firm's total credits with expiry warnings.
 * Shows a loading state while fetching data and handles error states gracefully.
 * 
 * Features:
 * - Displays total available credits
 * - Warning color/icon when credits are low (< 10)
 * - Error color when no credits available
 * - Tooltip showing expiring credits information when applicable
 * - Loading state with spinner
 */
export function CreditBadge({ 
  size = 'small', 
  showCount = true,
  onClick 
}: CreditBadgeProps) {
  const { currentFirmId } = useFirm();
  const { user } = useAuth();
  
  // Fetch firm data with credit information
  const { 
    data: firmData, 
    isLoading, 
    error 
  } = useGetFirmAsUser(currentFirmId || '', {
    query: {
      enabled: !!currentFirmId,
      // Refetch every 5 minutes to keep credit info fresh
      refetchInterval: 5 * 60 * 1000,
      queryKey: [`/firms/${currentFirmId}`],
    }
  });

  // Don't render if no firm selected or no permission to view credits
  if (!currentFirmId || !canViewBasicCreditInfo(user?.role)) {
    return null;
  }

  // Loading state
  if (isLoading) {
    return (
      <Chip
        icon={<CircularProgress size={16} />}
        label={showCount ? "Loading..." : ""}
        size={size}
        variant="outlined"
        sx={{ 
          minWidth: showCount ? 100 : 'auto',
          '& .MuiCircularProgress-root': {
            color: 'text.secondary'
          }
        }}
      />
    );
  }

  // Error state
  if (error || !firmData) {
    return (
      <Chip
        icon={<WarningIcon />}
        label={showCount ? "Error" : ""}
        size={size}
        color="error"
        variant="outlined"
        sx={{ minWidth: showCount ? 100 : 'auto' }}
      />
    );
  }

  const { totalCredits, expiringCredits } = firmData;

  // Determine badge color based on credit levels
  const getBadgeColor = () => {
    if (totalCredits === 0) return 'error';
    if (totalCredits < 10) return 'warning';
    return 'success';
  };

  const getBadgeVariant = () => {
    if (expiringCredits > 0) return 'filled';
    return 'outlined';
  };

  // Create tooltip content for expiring credits
  const getTooltipContent = () => {
    if (expiringCredits > 0) {
      return (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Credit Status
          </Typography>
          <Typography variant="body2">
            Total Credits: {totalCredits}
          </Typography>
          <Typography variant="body2" color="warning.main">
            ⚠️ {expiringCredits} credits expiring in 30 days
          </Typography>
        </Box>
      );
    }
    
    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Credit Status
        </Typography>
        <Typography variant="body2">
          Total Credits: {totalCredits}
        </Typography>
        {totalCredits === 0 && (
          <Typography variant="body2" color="error.main">
            No credits available
          </Typography>
        )}
        {totalCredits > 0 && totalCredits < 10 && (
          <Typography variant="body2" color="warning.main">
            Low credit balance
          </Typography>
        )}
      </Box>
    );
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <Tooltip 
      title={getTooltipContent()}
      arrow
      placement="bottom"
    >
      <Chip
        icon={<AccountBalanceWalletIcon />}
        label={showCount ? `${totalCredits} Credits` : ''}
        color={getBadgeColor()}
        size={size}
        variant={getBadgeVariant()}
        onClick={handleClick}
        clickable={!!onClick}
        sx={{ 
          minWidth: showCount ? 120 : 'auto',
          fontWeight: 500,
          '& .MuiChip-icon': {
            fontSize: size === 'small' ? '1rem' : '1.25rem'
          },
          ...(onClick && {
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          })
        }}
        aria-label={`${totalCredits} credits available${expiringCredits > 0 ? `, ${expiringCredits} expiring soon` : ''}`}
      />
    </Tooltip>
  );
}

/**
 * Compact version of CreditBadge for use in headers or tight spaces
 */
export function CreditBadgeCompact({ onClick }: { onClick?: () => void }) {
  return (
    <CreditBadge 
      size="small"
      showCount={true}
      onClick={onClick}
    />
  );
}